import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { OverlayContainer, CellGrid, Cell } from './index.styles';

type DragItem = {
  x: number;
  y: number;
  w: number;
  h: number;
};

export type GhostCellOverlayHandle = {
  update: (dragItem: DragItem | null) => void;
};

type GhostCellOverlayProps = {
  cols: number;
  rowHeight: number;
  margin: [number, number];
  containerWidth: number;
  totalRows: number;
};

const isCellHighlighted = (col: number, row: number, dragItem: DragItem | null): boolean => {
  if (!dragItem) return false;

  return col >= dragItem.x && col < dragItem.x + dragItem.w && row >= dragItem.y && row < dragItem.y + dragItem.h;
};

export const GhostCellOverlay = forwardRef<GhostCellOverlayHandle, GhostCellOverlayProps>(
  ({ cols, rowHeight, margin, containerWidth, totalRows }, ref) => {
    const cellRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const cellWidth = (containerWidth - margin[0] * (cols - 1)) / cols;

    const applyHighlights = useCallback((dragItem: DragItem | null) => {
      cellRefs.current.forEach((el, key) => {
        const separatorIndex = key.indexOf('-');
        const col = Number(key.slice(0, separatorIndex));
        const row = Number(key.slice(separatorIndex + 1));

        el.dataset.highlighted = isCellHighlighted(col, row, dragItem) ? 'true' : 'false';
      });
    }, []);

    useImperativeHandle(ref, () => ({ update: applyHighlights }), [applyHighlights]);

    const cells = useMemo(() => {
      const result: Array<{ col: number; row: number }> = [];

      for (let row = 0; row < totalRows; row++) {
        for (let col = 0; col < cols; col++) {
          result.push({ col, row });
        }
      }

      return result;
    }, [cols, totalRows]);

    return (
      <OverlayContainer data-testid="ghost-cell-overlay">
        <CellGrid
          style={{
            gridTemplateColumns: `repeat(${cols}, ${cellWidth}px)`,
            gridTemplateRows: `repeat(${totalRows}, ${rowHeight}px)`,
            gap: `${margin[1]}px ${margin[0]}px`,
          }}
        >
          {cells.map(({ col, row }) => (
            <Cell
              key={`ghost-${col}-${row}`}
              ref={(el) => {
                if (el) cellRefs.current.set(`${col}-${row}`, el);
                else cellRefs.current.delete(`${col}-${row}`);
              }}
              data-highlighted="false"
              data-testid={`ghost-cell-${col}-${row}`}
            />
          ))}
        </CellGrid>
      </OverlayContainer>
    );
  }
);

GhostCellOverlay.displayName = 'GhostCellOverlay';
